import _ from 'lodash'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import Box from '@mui/material/Box'
import Fab from '@mui/material/Fab'
import Stack from '@mui/material/Stack'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'

import { HDSTETH_RTC_DEVICE, isHDStethDeviceId, useHDStethDevice } from '@/hooks/useHDStethDevice'
import { useLazyAudioNode, useOwnedAudioContext } from '@/hooks/useOwnedAudioContext'
import useMediaDevices from '@/hooks/useMediaDevices'

import { useDiagnosticRTC } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import { usePeer } from '@/providers/PeerjsProvider'

import { getMedicalCameras, getStethoscopes, getUserMedia } from '@/utils/devices'

import AudioGainControl from '../components/AudioGainControl'
import LiveAudioVisualizer, { LiveAudioVisualizerHandle } from '../components/LiveAudioVisualizer'
import PeerConnectionIndicator from '../components/PeerConnectionIndicator'
import { DiagnosticRTCServiceHandle, DiagnosticRTCServiceProps } from '../types'
import { captureVideoScreenshot, microphonePluginConstraintsGenerator, selectDeviceWithRetry } from '../utils'
import { createAudioProcessingChain, getFirstAudioTrackLabel, normalizeLabel } from '../components/utils'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { HDStethEcgProvider } from '../hd-steth-ecg-provider'
import { createPeerEcgTransport } from '@/utils/hdSteth/peer-ecg-transport'

type PeerjsProps = DiagnosticRTCServiceProps & {
  children: React.ReactNode
  streamer: string
}

function Peerjs(props: PeerjsProps, ref: React.Ref<DiagnosticRTCServiceHandle>) {
  const { children, customDevices, onDevicesChange, onStreamChange, streamer, type } = props
  const { activeDiagnosticRTCService, isStreamer } = useDiagnosticRTC()
  const mediaDevices = useMediaDevices()
  const hdSteth = useHDStethDevice(type === 'audio' && isStreamer)
  const { callConnectedPeers, connect, connectedPeers, disconnect, isReady, setStream, stream, sendDiagnosticData, subscribeDiagnosticData } = usePeer()
  const ecgTransport = useMemo(() => createPeerEcgTransport(streamer, sendDiagnosticData, subscribeDiagnosticData), [sendDiagnosticData, subscribeDiagnosticData, streamer])
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const audioContextRef = useOwnedAudioContext()
  const gainNodeRef = useLazyAudioNode(audioContextRef, (audioContext) => audioContext.createGain()) // For output gain control

  const audioRef = useRef<HTMLAudioElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const liveAudioVisualizerRef = useRef<LiveAudioVisualizerHandle>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const hdStethStreamRef = useRef<MediaStream | null>(null)

  const [selectedDeviceId, setSelectedDeviceId] = useState<string>()

  const [filterType, setFilterType] = useState<'bell' | 'diaphragm' | 'raw'>('raw')

  const { medicalCameras, videoMediaDevices } = useMemo(() => {
    const medicalCameras = _.map(getMedicalCameras(customDevices, mediaDevices), (medicalCameraDevice) => ({
      id: medicalCameraDevice._id,
      device: {
        deviceId: medicalCameraDevice.device.deviceId,
        label: medicalCameraDevice.device.name,
        kind: medicalCameraDevice.device.type,
      },
      customName: medicalCameraDevice.customName,
      iconURL: medicalCameraDevice.iconURL,
      type: medicalCameraDevice.type,
    }))

    const videoMediaDevices = _.filter(mediaDevices, (device) => device.kind === 'videoinput')

    return { medicalCameras, videoMediaDevices }
  }, [customDevices, mediaDevices])

  const { audioInputMediaDevices, stethoscopes } = useMemo(() => {
    const stethoscopes = _.map(getStethoscopes(customDevices, mediaDevices), (stethoscopeDevice) => ({
      id: stethoscopeDevice._id,
      device: {
        deviceId: stethoscopeDevice.device.deviceId,
        label: stethoscopeDevice.device.name,
        kind: stethoscopeDevice.device.type,
      },
      customName: stethoscopeDevice.customName,
      iconURL: stethoscopeDevice.iconURL,
      type: stethoscopeDevice.type,
    }))

    // Surface the HD Steth as a selectable stethoscope while it is streaming.
    // It isn't an OS audio device, so it's injected here rather than discovered.
    if (hdSteth.available) stethoscopes.push(HDSTETH_RTC_DEVICE)

    const audioInputMediaDevices = _.filter(mediaDevices, (device) => device.kind === 'audioinput')

    return { audioInputMediaDevices, stethoscopes }
  }, [customDevices, mediaDevices, hdSteth.available])

  useImperativeHandle(ref, () => ({
    captureScreen,
    getStream,
    refreshConnection,
    renderAudio,
    renderAudioVisualizer,
    renderVideo,
    selectDevice,
    shareScreen,
    stopMicro,
    stopVideo,
  }))

  const captureScreen = useCallback((cb: (imageFile: File) => void) => {
    const videoElement = videoRef.current
    if (!videoElement) return
    const file = captureVideoScreenshot(videoElement)
    cb(file)
  }, [])

  const getStream = useCallback(() => stream, [stream])

  const refreshConnection = useCallback(() => {
    if (!isStreamer) {
      disconnect()
      connect(streamer)
      return
    }

    if (stream) callConnectedPeers(stream)
  }, [callConnectedPeers, connect, disconnect, isStreamer, stream, streamer])

  const renderAudio = useCallback(() => <audio ref={audioRef} />, [])

  const renderFilterSelector = useCallback(() => {
    return (
      <FormControl variant="outlined" fullWidth>
        <InputLabel>{t('component.group.stethoscope.filters.label')}</InputLabel>
        <Select value={filterType} onChange={(event: any) => setFilterType(event.target.value)} label="Filter Type">
          <MenuItem value="raw">{t('component.group.stethoscope.filters.raw.label')}</MenuItem>
          <MenuItem value="bell">{t('component.group.stethoscope.filters.bell.label')}</MenuItem>
          <MenuItem value="diaphragm">{t('component.group.stethoscope.filters.diaphragm.label')}</MenuItem>
        </Select>
      </FormControl>
    )
  }, [filterType])

  const renderAudioVisualizer = useCallback(() => {
    return (
      <>
        <Stack direction="row" spacing={isStreamer && connectedPeers.length > 0 ? 2 : 1} p={1}>
          <PeerConnectionIndicator />
          <AudioGainControl
            initialGain={isStreamer ? 0 : 5}
            onGainChange={(gain) => {
              // Update audio element volume for direct playback
              if (audioRef.current) {
                audioRef.current.volume = Math.min(gain / 10, 1) // Convert to 0-1 range, cap at 1
              }
            }}
          />
          {!isStreamer && (
            <Fab color="primary" onClick={refreshConnection} size="small">
              <RefreshRoundedIcon />
            </Fab>
          )}
        </Stack>
        <LiveAudioVisualizer stream={stream ?? undefined} />
      </>
    )
  }, [connectedPeers, isStreamer, refreshConnection, stream, renderFilterSelector])

  const renderVideo = useCallback(
    () => (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <video autoPlay playsInline ref={videoRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}></video>
      </Box>
    ),
    []
  )

  const selectDevice = useCallback(
    (deviceId: string) => {
      if (!isStreamer) return
      if (type === 'audio' && isHDStethDeviceId(deviceId)) {
        // HD Steth: not a capture device, so skip getUserMedia and publish the
        // synthetic socket-fed stream through the same processing + peer path.
        const hdStream = hdSteth.createStream()
        if (!hdStream) {
          console.error(`❌ HD STETH STREAM NOT READY:`, { deviceId })
          return
        }
        ;(async () => {
          await hdSteth.resume() // bridge AudioContext needs a user gesture
          if (audioContextRef.current.state === 'suspended') await audioContextRef.current.resume()

          streamRef.current?.getAudioTracks().forEach((track) => track.stop())
          hdStethStreamRef.current?.getAudioTracks().forEach((track) => track.stop())
          hdStethStreamRef.current = hdStream

          const destination = createAudioProcessingChain(audioContextRef.current, 'raw', hdStream)
          const processedStream = destination.stream

          console.warn('🩺 PEERJS HD STETH DEVICE ACTIVATED — publishing synthetic heart-sound stream')

          setSelectedDeviceId(deviceId)
          setStream(processedStream)
          streamRef.current = processedStream
          callConnectedPeers(processedStream)
        })()
        return
      }
      if (type === 'audio') {
        console.warn(`🎤 REQUESTING AUDIO DEVICE:`, { deviceId })

        getUserMedia(microphonePluginConstraintsGenerator(deviceId), (error, initialStream) => {
          if (error) {
            console.error(`❌ GETUSERMEDIA ERROR:`, {
              deviceId,
              error: String(error),
              errorName: 'GetUserMediaError',
            })
            return
          }
          if (!initialStream) {
            console.error(`❌ NO STREAM RETURNED:`, { deviceId })
            return
          }

          // Disconnect old stream nodes before connecting new ones
          if (streamRef.current) {
            streamRef.current.getAudioTracks().forEach((track) => track.stop())
          }
          // A previously selected HD Steth output is no longer needed either.
          hdStethStreamRef.current?.getAudioTracks().forEach((track) => track.stop())
          hdStethStreamRef.current = null
          ;(async () => {
            const { workingStream, requestedLabel, actualTrackLabel, mismatch } = await selectDeviceWithRetry(deviceId, initialStream, 'Peerjs')

            if (mismatch) {
              enqueueSnackbar(
                t('component.ui.deviceSelectionDialog.deviceMismatch', {
                  requested: requestedLabel || deviceId,
                  actual: actualTrackLabel,
                  defaultValue: 'Could not start "{{requested}}". Using "{{actual}}" instead.',
                }),
                { variant: 'warning', autoHideDuration: 8000 }
              )
            }

            // Build processed stream now from the finalized workingStream
            const destination = createAudioProcessingChain(audioContextRef.current, 'raw', workingStream)
            const processedStream = destination.stream

            console.warn(`🎵 AUDIO DEVICE SELECTED:`, {
              requestedDeviceId: deviceId,
              requestedDeviceName: requestedLabel || 'Unknown',
              actualTrackLabel,
              actualTrackId: workingStream.getAudioTracks()[0]?.id || 'No track ID',
              deviceMismatch: mismatch,
            })

            setSelectedDeviceId(deviceId)
            setStream(processedStream)
            streamRef.current = processedStream
            callConnectedPeers(processedStream)
          })()
        })
      }
      if (type === 'video') {
        getUserMedia(
          {
            audio: false,
            video: {
              deviceId: {
                exact: deviceId,
              },
            },
          },
          (error, stream?: MediaStream) => {
            if (error) {
              console.error(`❌ VIDEO DEVICE ERROR:`, {
                requestedDeviceId: deviceId,
                error: (error as any)?.message || 'Unknown error',
              })
              enqueueSnackbar(t('notification.mediaDevice.getUserMedia.error'), { variant: 'error' })
            } else {
              if (stream) {
                // Log warning about selected video device
                const selectedDevice = [...medicalCameras, ...videoMediaDevices].find(
                  (device) => (device as any).device?.deviceId === deviceId || (device as any).deviceId === deviceId
                )
                console.warn(`📹 VIDEO DEVICE SELECTED:`, {
                  requestedDeviceId: deviceId,
                  requestedDeviceName: (selectedDevice as any)?.device?.label || (selectedDevice as any)?.label || 'Unknown',
                  actualTrackLabel: stream.getVideoTracks()[0]?.label || 'No track label',
                  actualTrackId: stream.getVideoTracks()[0]?.id || 'No track ID',
                  isMedicalCamera: (selectedDevice as any)?.type === 'medicalCamera',
                })

                setStream(stream)
                callConnectedPeers(stream)
                setSelectedDeviceId(deviceId)
              }
            }
          }
        )
      }
    },
    [callConnectedPeers, enqueueSnackbar, hdSteth, isStreamer, setStream, t, type]
  )

  const shareScreen = useCallback(
    (isScreenSharing: boolean) => {
      if (isScreenSharing) {
        navigator.mediaDevices
          .getDisplayMedia({ video: true })
          .then((screenStream) => {
            setStream(screenStream)
            callConnectedPeers(screenStream)
          })
          .catch(console.log)
      } else {
        setStream(null)
        if (selectedDeviceId) selectDevice(selectedDeviceId)
      }
    },
    [callConnectedPeers, selectDevice, selectedDeviceId, setStream]
  )

  const stopMicro = useCallback(() => {
    setSelectedDeviceId(undefined)
    liveAudioVisualizerRef.current?.stop()
    disconnect()
  }, [disconnect])

  const stopVideo = useCallback(() => {
    setStream(null)
  }, [setStream])

  useEffect(() => {
    return () => {
      disconnect()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const audioElement = audioRef.current
    const gainNode = gainNodeRef.current

    // Set initial gain value, e.g., 1 for no change.
    gainNode.gain.value = !isStreamer ? 5 : 1

    // The context itself is owned (and closed on unmount) by useOwnedAudioContext.
    return () => {
      gainNode.disconnect()
    }
  }, [isStreamer])

  useEffect(() => {
    const audioElement = audioRef.current
    const processedStream = stream // This is now the processed stream

    if (!audioElement || !processedStream || type !== 'audio') return

    // Set the audio element's source to the processed stream
    audioElement.srcObject = processedStream
    // Allow local monitoring for both streamer and listener
    audioElement.muted = false
    // Set volume based on gain control (convert from 0-40 range to 0-1 range)
    audioElement.volume = Math.min(gainNodeRef.current.gain.value / 10, 1)
    audioElement.onloadedmetadata = () => {
      audioElement.play()
    }

    // Cleanup to stop tracks and prevent memory leaks
    return () => {
      processedStream.getAudioTracks().forEach((track) => {
        track.stop()
      })
    }
  }, [stream, type, isStreamer])

  useEffect(() => {
    const videoElement = videoRef.current
    if (!videoElement || !stream || type !== 'video') return
    videoElement.srcObject = stream
    videoElement.muted = true
    videoElement.onloadedmetadata = () => {
      videoElement.play()
    }

    return () => {
      stream.getVideoTracks().forEach((track) => {
        track.stop()
      })
    }
  }, [stream, type])

  // make peer connection to streamer
  useEffect(() => {
    if (!!isReady && !isStreamer) {
      connect(streamer)
    }
  }, [connect, isReady, isStreamer, streamer])

  useEffect(() => {
    onDevicesChange({ medicalCameras, stethoscopes, audioInputMediaDevices, videoMediaDevices })
  }, [activeDiagnosticRTCService, audioInputMediaDevices, medicalCameras, onDevicesChange, stethoscopes, videoMediaDevices])

  useEffect(() => {
    onStreamChange(stream)
  }, [onStreamChange, stream])

  return <HDStethEcgProvider enabled={type === 'audio' && !!stream} local={isStreamer} selectedDeviceId={selectedDeviceId} transport={ecgTransport}>{children}</HDStethEcgProvider>
}

export default forwardRef(Peerjs)
